import React, { useMemo } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { PolyglotComponentProps, getPolyglot } from '../../../i18n';
import { Group } from '../../../redux/groups/api/group.model';
import { RouterComponentProps } from '../../../util/route-dom';
import type { NavQueryParams } from '../Device/DeviceOverview/DeviceLayout';
import {
  getGroupByGroupIdSelector,
  getGroups,
} from '../../../redux/groups/selectors';
import { useAppSelector } from '../../../redux/store.model';
import { useGroupById } from '../../../redux/groups/hooks/useGroupById';
import { Skeleton } from '@mui/material';

export interface NavigationPathProps
  extends RouterComponentProps,
    PolyglotComponentProps {}

export interface NavigationPath {
  content: React.ReactNode;
  onClick: () => void;
}

/** basing in the last item on the path */
export function getModuleSelected(pathname: string) {
  const list = pathname.split('/');
  const moduleSelected = list[list.length - 1] ?? '';
  return moduleSelected;
}

const dicTitles: Record<string, string> = {
  incidents: 'incident_manager.title',
  recipes: 'recipe_manager.title',
  history: 'device.monitoring.title',
  settings: 'group.tooltip.open_window_edit',
  alarms: 'incident.all_alarms',
  reports: 'reports.reports',
  map: 'map.title',
};
/**
 * Navigation path
 * Build the navigation path used in the breadcrumb
 * @param props {NavigationPathProps} need router-dom and polyglot
 * @param string groupId
 * @param array groups
 * @param object group
 * @return array sectionsNavigation
 */
export function navigationPath(
  props: Pick<NavQueryParams, 'navigate' | 'polyglot'>,
  groupId: string | undefined,
  group: Group | undefined,
  moduleSelected?: string
): NavigationPath[] {
  const { polyglot, navigate } = props;

  const sectionsNavigation: NavigationPath[] = [
    {
      content: polyglot.t('nav.menu.devicemanager'),
      onClick: () => navigate('/devicemanager'),
    },
  ];

  /* build path */
  if (groupId && group !== undefined) {
    const pathToGroup = group.pathGroupsIds;
    pathToGroup.forEach((gId) => {
      sectionsNavigation.push({
        content: <GroupPathContent groupId={gId} />,
        onClick: () => navigate(`/devicemanager/group/${gId}`),
      });
    });
  }

  /** module selected */
  if (moduleSelected && Object.keys(dicTitles).includes(moduleSelected ?? '')) {
    sectionsNavigation.push({
      content: polyglot.t(dicTitles[moduleSelected] ?? ''),
      onClick: () =>
        navigate(`/devicemanager/group/${groupId}/${moduleSelected}`),
    });
  }
  return sectionsNavigation;
}

const GroupPathContent = ({ groupId }: { groupId: string | undefined }) => {
  const { data: group, isLoading } = useGroupById(groupId);

  if (isLoading) return <Skeleton variant="text" width={64} />;
  if (!group) return null;
  return <span>{group.name}</span>;
};

export function useNavigationGroupPath() {
  const params = useParams<{ groupId: string }>();
  const group = useAppSelector((state) =>
    getGroupByGroupIdSelector(state, params)
  );
  const location = useLocation();
  const navigate = useNavigate();
  const polyglot = getPolyglot();
  const groupId = params.groupId;
  const moduleSelected = useMemo(
    () => getModuleSelected(location.pathname),
    [location.pathname]
  );
  const sectionsNavigation = navigationPath(
    { navigate, polyglot },
    groupId,
    group,
    moduleSelected
  );
  return sectionsNavigation;
}
